import { Fragment, type ReactNode } from "react";
import type { AccentName } from "../../../lib/accents";
import { cn } from "../../../lib/cn";
import { BULLET_DOT } from "../categories";
import type { StoryEmbed } from "../contract";
import type { InlineSegment, StoryBlock } from "../format";

function Inline({ segments }: { segments: InlineSegment[] }) {
  return (
    <>
      {segments.map((segment, i) =>
        segment.bold ? (
          <strong key={i} className="font-semibold text-neutral-900">
            {segment.text}
          </strong>
        ) : (
          <Fragment key={i}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}

// Literal class sets per size (Tailwind can't see interpolated names).
const SIZE = {
  /** The write form's preview. */
  compact: {
    root: "space-y-5 text-base leading-7 text-neutral-800 sm:text-[17px] sm:leading-8",
    heading: "pt-3 font-display text-xl font-semibold leading-snug text-neutral-900 sm:text-2xl",
  },
  /** The article: Medium-sized type on a ~65ch measure, roomy headings. */
  reading: {
    root: "space-y-6 text-[18px] leading-8 text-neutral-800 sm:text-[20px] sm:leading-9",
    heading:
      "pt-6 font-display text-2xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-[1.875rem]",
  },
} as const;

/**
 * Renders parsed story blocks as plain React elements (never HTML), at a
 * comfortable reading size. Embeds are drawn by the caller.
 */
export function StoryBody({
  blocks,
  accent,
  renderEmbed,
  size = "compact",
  className,
}: {
  blocks: StoryBlock[];
  accent: AccentName;
  renderEmbed?: (name: StoryEmbed) => ReactNode;
  size?: keyof typeof SIZE;
  className?: string;
}) {
  const sizing = SIZE[size];
  return (
    <div className={cn(sizing.root, className)}>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "paragraph":
            return (
              <p key={i}>
                <Inline segments={block.segments} />
              </p>
            );
          case "heading":
            return (
              <h2 key={i} className={sizing.heading}>
                {block.text}
              </h2>
            );
          case "list":
            return (
              <ul key={i} className="space-y-2.5">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-3">
                    <span
                      aria-hidden
                      className={cn("mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full", BULLET_DOT[accent])}
                    />
                    <span>
                      <Inline segments={item} />
                    </span>
                  </li>
                ))}
              </ul>
            );
          case "embed":
            return renderEmbed ? (
              <div key={i} className="py-2 text-base leading-7">
                {renderEmbed(block.name)}
              </div>
            ) : null;
        }
      })}
    </div>
  );
}
